/**
 * Content-type detection from magic bytes.
 *
 * The client-supplied `Content-Type` header and the file extension are both
 * attacker-controlled: an HTML page uploaded as `logo.png` with
 * `image/png` declared will be sniffed and executed by browsers on whatever
 * origin serves it. Since storefront media is served from a CDN origin the
 * merchant's own customers trust, the declared type is treated as a hint and
 * the bytes as the truth.
 */

export interface SniffResult {
  mimeType: string;
  /** False when the bytes match nothing we allow. */
  recognized: boolean;
}

const SIGNATURES: Array<{ mime: string; test: (b: Buffer) => boolean }> = [
  { mime: 'image/jpeg', test: b => b.length > 3 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff },
  {
    mime: 'image/png',
    test: b =>
      b.length > 8 &&
      b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47 &&
      b[4] === 0x0d && b[5] === 0x0a && b[6] === 0x1a && b[7] === 0x0a,
  },
  { mime: 'image/gif', test: b => b.length > 6 && b.subarray(0, 6).toString('latin1').startsWith('GIF8') },
  {
    mime: 'image/webp',
    test: b =>
      b.length > 12 &&
      b.subarray(0, 4).toString('latin1') === 'RIFF' &&
      b.subarray(8, 12).toString('latin1') === 'WEBP',
  },
  {
    mime: 'image/avif',
    test: b => b.length > 12 && b.subarray(4, 8).toString('latin1') === 'ftyp' &&
      ['avif', 'avis'].includes(b.subarray(8, 12).toString('latin1')),
  },
  { mime: 'application/pdf', test: b => b.length > 4 && b.subarray(0, 4).toString('latin1') === '%PDF' },
  { mime: 'application/zip', test: b => b.length > 4 && b[0] === 0x50 && b[1] === 0x4b && (b[2] === 0x03 || b[2] === 0x05) },
];

export function sniffMimeType(buffer: Buffer): SniffResult {
  for (const sig of SIGNATURES) {
    if (sig.test(buffer)) return { mimeType: sig.mime, recognized: true };
  }
  return { mimeType: 'application/octet-stream', recognized: false };
}

export const ALLOWED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'];
export const ALLOWED_DOCUMENT_MIME_TYPES = ['application/pdf', 'text/plain', 'text/csv', 'application/zip'];

/**
 * SVG is rejected outright rather than sanitised (plan §20). A stored SVG is
 * an XSS vector on whatever origin serves it, and no sanitiser is worth
 * trusting on a public storefront origin for the sake of a logo format that
 * PNG covers.
 */
export function isRejectedType(mimeType: string, fileName: string): boolean {
  const lower = mimeType.toLowerCase();
  if (lower === 'image/svg+xml' || lower.startsWith('text/html')) return true;
  return /\.(svg|svgz|html?|xhtml)$/i.test(fileName);
}

export interface ImageValidation {
  ok: boolean;
  mimeType: string;
  reason?: string;
}

/** Validates an uploaded image by its bytes, returning the real type to store. */
export function validateImageUpload(buffer: Buffer, declaredType: string, fileName: string): ImageValidation {
  if (isRejectedType(declaredType, fileName)) {
    return { ok: false, mimeType: declaredType, reason: 'SVG and HTML uploads are not allowed.' };
  }
  const sniffed = sniffMimeType(buffer);
  if (!sniffed.recognized || !ALLOWED_IMAGE_MIME_TYPES.includes(sniffed.mimeType)) {
    return {
      ok: false,
      mimeType: sniffed.mimeType,
      reason: `File contents are not a supported image (detected ${sniffed.mimeType}).`,
    };
  }
  return { ok: true, mimeType: sniffed.mimeType };
}
